import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const THEME_STORAGE_KEY = "scsport-dashboard-theme";
const THEMES = {
  LIGHT: "light",
  DARK: "dark",
};

const ThemeContext = createContext(null);

const isValidTheme = (value) =>
  value === THEMES.LIGHT || value === THEMES.DARK;

const getSystemTheme = () => {
  if (typeof window === "undefined" || !window.matchMedia) {
    return THEMES.LIGHT;
  }

  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? THEMES.DARK
    : THEMES.LIGHT;
};

const getInitialTheme = () => {
  if (typeof window === "undefined") return THEMES.LIGHT;

  const savedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (isValidTheme(savedTheme)) return savedTheme;

  return getSystemTheme();
};

const applyTheme = (theme, { disableTransitions = false } = {}) => {
  if (typeof document === "undefined") return;

  const root = document.documentElement;
  const updateTheme = () => {
    root.dataset.theme = theme;
    root.style.colorScheme = theme === THEMES.DARK ? "dark" : "light";
  };

  if (!disableTransitions) {
    updateTheme();
    return;
  }

  root.dataset.themeChanging = "true";

  // Flush the transition override before mutating theme tokens.
  void root.offsetHeight;
  updateTheme();

  if (typeof window === "undefined" || !window.requestAnimationFrame) {
    delete root.dataset.themeChanging;
    return;
  }

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      delete root.dataset.themeChanging;
    });
  });
};

export const ThemeProvider = ({ children }) => {
  const hasMountedRef = useRef(false);
  const [theme, setTheme] = useState(() => {
    const initialTheme = getInitialTheme();
    applyTheme(initialTheme);
    return initialTheme;
  });

  useEffect(() => {
    applyTheme(theme, { disableTransitions: hasMountedRef.current });
    hasMountedRef.current = true;

    if (typeof window !== "undefined") {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    }
  }, [theme]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleThemeChange = () => {
      const savedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
      if (isValidTheme(savedTheme)) return;

      setTheme(mediaQuery.matches ? THEMES.DARK : THEMES.LIGHT);
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleThemeChange);
      return () => mediaQuery.removeEventListener("change", handleThemeChange);
    }

    mediaQuery.addListener(handleThemeChange);
    return () => mediaQuery.removeListener(handleThemeChange);
  }, []);

  const value = useMemo(
    () => ({
      theme,
      isDark: theme === THEMES.DARK,
      setTheme,
      toggleTheme: () =>
        setTheme((currentTheme) =>
          currentTheme === THEMES.DARK ? THEMES.LIGHT : THEMES.DARK,
        ),
    }),
    [theme],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error("useTheme must be used inside ThemeProvider");
  }

  return context;
};

export default ThemeProvider;
